import { Button } from "@/components/ui/baseUi/button"
import { useCurrentBox } from "@/modules/box/hooks/useBox"
import { BoxStaffRole } from "@/modules/boxStaff/gateway/boxStaffDto"
import { useChatClient } from "@/modules/chat/stores/useChatStore"
import { word } from "@/modules/core/utils/i18n"
import useDebounce from "@/modules/hooks/useDebounce"
import { cn } from "@/modules/lib/utils"
import { ChannelListHeader } from "@modules/chat/components/channelListHeader"
import { MessageSearchInput } from "@modules/chat/components/messageSearchInput"
import { MessageSearchList } from "@modules/chat/components/messageSearchList"
import { useChatPageContext } from "@modules/chat/contexts/chatPageContext"
import { usePaginatedChatSearch } from "@modules/chat/hooks/usePaginatedChatSearch"
import { CHANNEL_LIST_SHOWN_WINDOW_WIDTH } from "@modules/chat/utils/chatConstants"
import { useWindowSize } from "@modules/utils/hooks/useWindowsSize"
import { LoaderCircle } from "lucide-react"
import { useState } from "react"
import type { ChannelFilters, ChannelSort } from "stream-chat"
import {
  ChannelPreviewMessenger,
  type ChannelPreviewUIComponentProps,
  ChannelList as DefaultChannelList,
  LoadMorePaginator,
  getDisplayTitle,
} from "stream-chat-react"

const sort: ChannelSort = { last_message_at: -1 }
const options = {
  presence: true,
  state: true,
  watch: true,
}

export type ChannelListTypeProps = {
  filters: ChannelFilters
  className?: string
} & React.ComponentProps<"div">

export function ChannelList({ filters, className, ...props }: ChannelListTypeProps) {
  const [inputValue, setInputValue] = useState<string>("")

  const debouncedSearchTerm = useDebounce(inputValue, 300)
  const { result: box } = useCurrentBox()
  const chatClient = useChatClient()
  const { updateIsCreatingNewChannel, updateIsAddingMembersToChannel, displayChannelList, toggleDisplayChannelList } =
    useChatPageContext()
  const { loading, loadMore, loadingMore, searchResults, refreshing, refreshList, reset } =
    usePaginatedChatSearch(debouncedSearchTerm)
  const { width } = useWindowSize()

  const CustomListItem = (props: ChannelPreviewUIComponentProps) => {
    return (
      <ChannelPreviewMessenger
        onSelect={() => {
          updateIsCreatingNewChannel(false)
          updateIsAddingMembersToChannel(undefined)
          if (props.setActiveChannel) {
            props.setActiveChannel(props.channel, props.watchers)
            if (width <= CHANNEL_LIST_SHOWN_WINDOW_WIDTH) toggleDisplayChannelList()
          }
        }}
        {...props}
        displayTitle={getDisplayTitle(props.channel, chatClient?.user)}
      />
    )
  }

  const onInputChange = (text: string) => {
    setInputValue(text)
  }

  return (
    <div
      className={cn(
        "float-left flex h-[calc(100vh-9rem)] w-75 max-w-75 min-w-75 flex-col items-center justify-center overflow-hidden rounded-tl-xl border border-neutral-200 bg-surface-background transition-all duration-150 ease-linear",
        "md:w-42.5",
        { "mr-3": width > CHANNEL_LIST_SHOWN_WINDOW_WIDTH },
        { "max-w-full": displayChannelList },
        { "max-w-0 min-w-0": !displayChannelList },
        className,
      )}
      {...props}
    >
      <ChannelListHeader onResetSearch={() => onInputChange("")} />
      <MessageSearchInput
        search={{
          onChange: (event) => onInputChange(event.target.value),
          loading,
          value: inputValue,
          onReset: () => {
            reset()
            setInputValue("")
          },
        }}
        onFocus={() => {
          updateIsCreatingNewChannel(false)
          updateIsAddingMembersToChannel(undefined)
        }}
      />
      {debouncedSearchTerm ? (
        <MessageSearchList
          loading={loading}
          loadMore={() => {
            loadMore()
          }}
          loadingMore={loadingMore}
          searchResults={searchResults}
          refreshing={refreshing}
          refreshList={refreshList}
        />
      ) : (
        <DefaultChannelList
          filters={filters}
          sort={sort}
          options={options}
          LoadingIndicator={() => <></>}
          EmptyStateIndicator={() => <></>}
          LoadingErrorIndicator={() => <></>}
          Preview={CustomListItem}
          customActiveChannel={box && box.currentUserRole !== BoxStaffRole.READ_ONLY ? box.id : undefined}
          Paginator={(props) => (
            <LoadMorePaginator
              {...props}
              LoadMoreButton={({ isLoading, onClick }) => (
                <Button
                  size="sm"
                  disabled={isLoading}
                  onClick={onClick}
                  className="m-auto mt-1 block hover:bg-neutral-100"
                  isLoading={isLoading}
                >
                  {isLoading ? (
                    <LoaderCircle className="animate-spin" color="currentColor" size={13} />
                  ) : (
                    word("global.load_more")
                  )}
                </Button>
              )}
            />
          )}
        />
      )}
    </div>
  )
}
